import type { DefaultNodeTypes, SerializedBlockNode, SerializedLinkNode } from '@payloadcms/richtext-lexical';
import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import type { FormBlock, MediaBlock } from '@/payload-types';

import {
  type JSXConvertersFunction,
  LinkJSXConverter,
  RichText as RichTextWithoutBlocks,
} from '@payloadcms/richtext-lexical/react';

import { FormBlockJSXConverter } from '@/payload/richtext-editor/converters/formBlock';
import { MediaBlockJSXConverter } from '@/payload/richtext-editor/converters/mediaBlock';
import { UploadJSXConverter } from '@/payload/richtext-editor/converters/upload';
import { cn } from '@/utils/cn';

type NodeTypes = DefaultNodeTypes | SerializedBlockNode<FormBlock | MediaBlock>;

const internalDocToHref = ({ linkNode }: { linkNode: SerializedLinkNode }): string => {
  const { value, relationTo } = linkNode.fields.doc ?? {};

  if (typeof value !== 'object') {
    throw new Error('Expected value to be an object');
  }

  if (relationTo === 'media') {
    return `${value.url as string}`;
  }

  return `/${value.id}`;
};

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({ defaultConverters }) => ({
  ...defaultConverters,
  ...LinkJSXConverter({ internalDocToHref }),
  blocks: {
    formBlock: FormBlockJSXConverter.formBlock,
    mediaBlock: MediaBlockJSXConverter.mediaBlock,
  },
  upload: UploadJSXConverter.upload,
});

type Props = {
  data: SerializedEditorState;
} & React.HTMLAttributes<HTMLDivElement>;

export const RichText = ({ className, ...rest }: Props) => {
  return (
    <RichTextWithoutBlocks
      className={cn('prose max-w-none prose-headings:text-balance prose-p:text-balance', className)}
      converters={jsxConverters}
      {...rest}
    />
  );
};
